

import { Download } from 'lucide-react';
import { ResourceDisclosureHeader } from './ResourceDisclosure';
import { Button } from '@/components/ui/button';
import { useAtom } from 'jotai';
import { authenticationAtom } from "../State";


import { useEffect, useState } from 'react';
import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import { userAPI } from "../UserAPI";
import { Environment3DPropertiesSimple } from "../object-properties/building/Environment3DPropertiesSimple";

// Global state for expanded resources to persist across re-renders
const expandedResources = new globalThis.Map<string, boolean>();

export const LoD2Resource = ({ resource, stableKey }: { resource: any, stableKey?: string }) => {
    const resourceKey = stableKey || resource?.reference || resource?._id;
    const [expanded, setExpanded] = useState(() => expandedResources.get(resourceKey) || false);
    
    const [authentication] = useAtom(authenticationAtom);
    const [lod2Resource, setLod2Resource] = useState(null) as any;
    const [imageGenerated, setImageGenerated] = useState(false);

    useEffect(() => {
        let newResource: any = null;

        const call = async () => {
            if (resource.reference) {
                newResource = await userAPI.getResource(authentication.customer, resource.reference);
            } else {
                newResource = resource;
            }

            // Ensure options is always an object, never null or undefined
            if (!newResource.options) {
                newResource.options = {};
            }

            setLod2Resource(newResource);
            
            // Debug the resource data structure
            console.log('🏢 [LoD2] Resource data loaded:', {
                hasData: !!newResource.data,
                dataKeys: newResource.data ? Object.keys(newResource.data) : [],
                hasBuildings: !!newResource.data?.buildings,
                buildingsLength: newResource.data?.buildings?.length || 0,
                hasParcels: !!newResource.data?.parcels,
                parcelsLength: newResource.data?.parcels?.length || 0,
                fullData: newResource.data
            });
            
            // Check if buildings are in a different location
            if (newResource.data) {
                console.log('🏢 [LoD2] All data keys:', Object.keys(newResource.data));
                console.log('🏢 [LoD2] Data values:', Object.entries(newResource.data).map(([key, value]) => ({
                    key,
                    type: typeof value,
                    isArray: Array.isArray(value),
                    length: Array.isArray(value) ? value.length : 'N/A'
                })));
                
                // Check if data is an array and look for buildings/parcels in the array elements
                if (Array.isArray(newResource.data)) {
                    console.log('🏢 [LoD2] Data is an array, checking elements for buildings/parcels...');
                    newResource.data.forEach((item: any, index: number) => {
                        if (item && typeof item === 'object') {
                            console.log(`🏢 [LoD2] Array element ${index}:`, {
                                keys: Object.keys(item),
                                hasBuildings: !!item.buildings,
                                hasParcels: !!item.parcels,
                                buildingsLength: item.buildings?.length || 0,
                                parcelsLength: item.parcels?.length || 0
                            });
                        }
                    });
                }
            }
        }

        // Only call if we have a reference or if we don't have local data yet
        if (resource.reference || !lod2Resource) {
            call();
        }
    }, [resource.reference, resource._id, authentication.customer]);

    // Render 3D view offscreen and capture canvas as PNG
    const renderLoD2ForCapture = async (mapResource: any): Promise<{ dataURL: string, cleanup: () => void } | null> => {
        // Create a temporary container for offscreen rendering
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.left = '-9999px';
        tempContainer.style.top = '-9999px';
        tempContainer.style.width = '800px';
        tempContainer.style.height = '600px';
        tempContainer.style.visibility = 'hidden';
        document.body.appendChild(tempContainer);

        const root = ReactDOM.createRoot(tempContainer);
        try {
            // Mount the same component used in the visible view
            root.render(
                React.createElement(Environment3DPropertiesSimple, {
                    object: {
                        buildings: mapResource.data?.buildings,
                        parcels: mapResource.data?.parcels,
                        shape: mapResource.data?.shape || [{ lat: 50.106277, lon: 8.669347 }]
                    }
                })
            );

            // Heuristic wait for Mapbox to fully initialize and render
            await new Promise(resolve => setTimeout(resolve, 6000));

            // Try to find the Mapbox canvas inside our container
            const canvas = tempContainer.querySelector('canvas');
            if (!canvas || !(canvas instanceof HTMLCanvasElement) || canvas.width === 0 || canvas.height === 0) {
                // Allow a little more time if canvas is not ready yet
                await new Promise(resolve => setTimeout(resolve, 2000));
            }

            const finalCanvas = tempContainer.querySelector('canvas') as HTMLCanvasElement | null;
            if (!finalCanvas || finalCanvas.width === 0 || finalCanvas.height === 0) {
                throw new Error('Canvas not ready for capture');
            }

            const dataURL = finalCanvas.toDataURL('image/png');
            const cleanup = () => {
                try {
                    root.unmount();
                    document.body.removeChild(tempContainer);
                } catch { /* noop */ }
            };

            return { dataURL, cleanup };
        } catch (e) {
            try {
                root.unmount();
                document.body.removeChild(tempContainer);
            } catch { /* noop */ }
            console.error('❌ [LoD2 DOWNLOAD] Error during offscreen render:', e);
            return null;
        }
    };

    // Interactive export handler
    const exportLoD2AsPNG = async () => {
        if (!lod2Resource) {
            console.warn('❌ [LoD2 DOWNLOAD] Resource not available for export');
            return;
        }

        try {
            const result = await renderLoD2ForCapture(lod2Resource);
            if (!result) return;
            const { dataURL, cleanup } = result;

            // Trigger browser download
            const downloadLink = document.createElement('a');
            downloadLink.href = dataURL;
            downloadLink.download = `${lod2Resource?.title || 'lod2'}.png`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);

            cleanup();
        } catch (error) {
            console.error('❌ [LoD2 DOWNLOAD] Error exporting LoD2 view:', error);
        }
    };

    // Background generation and server upload (image pipeline)
    const generateAndStoreImage = async (retryCount = 0) => {
        if (!lod2Resource || !lod2Resource._id || imageGenerated) {
            return;
        }

        // Skip if image already persisted
        if (lod2Resource.imageGenerated || lod2Resource.hasImage) {
            setImageGenerated(true);
            return;
        }

        // Register upload
        if ((window as any).imageUploadTracker) {
            (window as any).imageUploadTracker.registerUpload(lod2Resource._id, 'lod2');
        }

        try {
            const result = await renderLoD2ForCapture(lod2Resource);
            if (!result) {
                throw new Error('Failed to render LoD2 for capture');
            }

            const { dataURL, cleanup } = result;

            // Validate dataURL
            if (!dataURL || dataURL === 'data:,' || dataURL.length < 1000) {
                cleanup();
                throw new Error('Generated image is empty or too small');
            }

            if ((window as any).imageUploadTracker) {
                (window as any).imageUploadTracker.updateStatus(lod2Resource._id, 'uploading');
            }

            const storeResult = await userAPI.storeResourceImage(authentication.customer, 'lod2', lod2Resource._id, dataURL);
            if (storeResult && storeResult.success) {
                if ((window as any).imageUploadTracker) {
                    (window as any).imageUploadTracker.updateStatus(lod2Resource._id, 'completed');
                }
                setImageGenerated(true);
            } else {
                throw new Error(storeResult?.error || 'Upload failed');
            }

            cleanup();
        } catch (error) {
            // Retry with small backoff
            if (retryCount < 2) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                await generateAndStoreImage(retryCount + 1);
            } else {
                console.error(`❌ [LoD2 UPLOAD] Upload failed after retries: ${lod2Resource?._id}`, error);
                if ((window as any).imageUploadTracker) {
                    (window as any).imageUploadTracker.updateStatus(lod2Resource._id, 'failed');
                }
            }
        }
    };

    // Trigger background generation when used in streaming context
    useEffect(() => {
        if (lod2Resource && resource._streamingContext && !imageGenerated) {
            generateAndStoreImage(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lod2Resource?._id, resource._streamingContext]);

    // Hinweis: Der Bestand zeichnete hier ein Symbol "CubeShape", für das die
    // Icon-Registry keine Entsprechung kennt — es blieb unsichtbar. Deshalb steht
    // vor dem Titel bewusst kein Symbol.
    return <div className="box-border w-full">
        {/* Der Inhalt ist eine 3D-Ansicht: sie misst beim Aufbau ihren Container
            und bliebe in einer zusammengeklappten Zeile auf 0 Pixel Höhe stehen.
            Deshalb hier weiter bedingtes Rendern statt der Grid-Animation — nur
            Kopfzeile und Chevron folgen dem gemeinsamen Muster. */}
        <ResourceDisclosureHeader
            expanded={expanded}
            onToggle={(e) => {
                if (e) {
                    e.preventDefault();
                    e.stopPropagation();
                }
                const newExpanded = !expanded;
                setExpanded(newExpanded);
                // Store in global state to persist across re-renders
                expandedResources.set(resourceKey, newExpanded);
            }}
            testId="chat-resource-lod2-toggle"
            title={lod2Resource?.title}
            actions={lod2Resource && (
                <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    data-testid="chat-resource-lod2-download"
                    className="h-8 w-8 shrink-0"
                    title="Download as PNG"
                    aria-label="Download as PNG"
                    onClick={exportLoD2AsPNG}
                >
                    <Download size={16} />
                </Button>
            )}
        />
        {expanded && lod2Resource
            ?
            <div className="mt-[25px]">
                <Environment3DPropertiesSimple object={{
                    buildings: lod2Resource.data?.buildings,
                    parcels: lod2Resource.data?.parcels,
                    shape: lod2Resource.data?.shape || [{lat: 50.106277, lon: 8.669347}]
                }} />
            </div>
            :
            null
        }
    </div>
}
